import React, { useEffect, useState } from 'react';
import {
  Typography,
  Checkbox,
  Select,
  MenuItem,
  // Grid,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import { get, isEmpty } from 'lodash';

import { Button, WrappedTextInput, Box } from 'components';
import { signUp } from 'redux-modules/auth/Actions';
import { Logo } from 'assets/index';
import { RootState } from 'redux-modules/store/rootState';
import { ISignUpModal } from 'common/types';
import { SignUpSchema } from 'common/utils/validations';
import history from 'common/utils/history';
import { checkReferralEmailRegistered } from 'services';
// import { urlConstants } from 'common/utils/constants';

import styles from './styles';
import SignInImage from '../SignInImage';

const SignUp = () => {
  const dispatchAction = useDispatch();
  const classes = styles();

  const search = history.location.search;
  const referralCode = new URLSearchParams(search).get('referral');
  const referralEmail = new URLSearchParams(search).get('email');

  const { isLoading, message } = useSelector(
    ({ Auth }: RootState) => Auth.signUp,
  );

  const { handleSubmit, control, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(SignUpSchema),
  });

  const socialMediaOption: Array<any> = [
    { text: 'AngelList', value: 'anglelistUrl' },
    { text: 'Crunchbase', value: 'crunchbaseUrl' },
    { text: 'Linkedin', value: 'linkedinUrl' },
    { text: 'Twitter', value: 'twitterUrl' },
    { text: 'Other', value: 'otherUrl' },
  ];

  const [selectedOptions, setSelectedOptions] = React.useState([
    'anglelistUrl',
  ]);
  const [checkingSignUpEmail, setCheckingSignUpEmail] = useState<boolean>(
    false,
  );
  const [referralEmailExists, setReferralEmailExists] = useState<boolean>(
    false,
  );

  const handleChangeOption = (value: string, index: number) => {
    const options = [...selectedOptions];
    options[index] = value;
    setValue(selectedOptions[index], '');
    setSelectedOptions(options);
  };

  const addSocialOption = (currentIndex: number) => {
    const options = [...selectedOptions];
    if (options.length === currentIndex + 1) {
      const newSocialMediaOption = socialMediaOption.filter(
        (socialMedia: any) => {
          if (options.indexOf(socialMedia.value) === -1) return socialMedia;
        },
      );
      if (!isEmpty(newSocialMediaOption)) {
        options[selectedOptions.length] = newSocialMediaOption[0].value;
        setSelectedOptions(options);
      }
    }
  };

  const removeSocialOption = (value: string, index: number) => {
    const options = [...selectedOptions];
    options.splice(index, 1);
    setSelectedOptions(options);
  };

  const onSubmit = ({
    name,
    email,
    password,
    linkedinUrl,
    crunchbaseUrl,
    anglelistUrl,
    twitterUrl,
    otherUrl,
    termsAccepted,
  }: ISignUpModal) => {
    dispatchAction(
      signUp({
        name,
        email,
        password,
        linkedinUrl,
        crunchbaseUrl,
        anglelistUrl,
        twitterUrl,
        otherUrl,
        termsAccepted,
        referralCode,
      }),
    );
  };

  useEffect(() => {
    if (referralEmail) {
      setCheckingSignUpEmail(true);
      setReferralEmailExists(false);
      checkReferralEmailRegistered(referralEmail)
        .then((res: any) => {
          if (res) {
            setReferralEmailExists(true);
          }
        })
        .finally(() => setCheckingSignUpEmail(false));
    }
  }, []);

  return (
    <Box className={classes.signUpContainer}>
      <Box className={classes.gridContainer}>
        <Box className={classes.leftSide}>
          {checkingSignUpEmail ? (
            <Box className={classes.loader}>
              <CircularProgress color="inherit" size={40} />
            </Box>
          ) : (
            <Box className={classes.leftSideContent}>
              <Box className={classes.imgBox}>
                <img src={Logo} alt={Logo} className={classes.logoIcon} />
              </Box>
              {referralEmailExists && (
                <Box className={classes.referralRegisteredBox}>
                  <Typography
                    variant="body1"
                    className={classes.errorReferralRegistered}
                  >
                    You are already registered.
                  </Typography>
                  <Typography className={classes.alreadyAcText}>
                    Already have account ?
                    <a
                      onClick={() => history.push('/sign-in')}
                      rel="noreferrer"
                      className={classes.signInLink}
                    >
                      {' '}
                      Back to login
                    </a>
                  </Typography>
                </Box>
              )}
              {!referralEmailExists && (
                <>
                  <Typography
                    variant="h2"
                    gutterBottom
                    className={classes.welcome8vdx}
                  >
                    Sign Up
                  </Typography>
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    data-testid="investorSignUpForm"
                    className={classes.validateForm}
                  >
                    <Typography className={classes.labelText} variant="body1">
                      Name *
                    </Typography>
                    <Controller
                      name="name"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <Box className={classes.controllerBox}>
                          <WrappedTextInput
                            type="text"
                            placeholder="Investor's Full Name"
                            className={classes.textValInput}
                            InputProps={{
                              classes: { root: classes.inputRoot },
                            }}
                            InputLabelProps={{
                              classes: {
                                root: classes.labelRoot,
                                focused: classes.labelFocused,
                              },
                            }}
                            value={value}
                            onChange={(e: any) =>
                              onChange(get(e, 'target.value').trimStart())
                            }
                            error={!!error?.message}
                          />
                          {error?.message && (
                            <span className={classes.errorText}>
                              {error?.message}
                            </span>
                          )}
                        </Box>
                      )}
                      rules={{ required: "Investor's Name is required" }}
                    />
                    <Typography className={classes.labelText} variant="body1">
                      Email *
                    </Typography>
                    <Controller
                      name="email"
                      control={control}
                      defaultValue={referralEmail}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <Box className={classes.controllerBox}>
                          <WrappedTextInput
                            type="text"
                            placeholder="Your email ID"
                            className={classes.textValInput}
                            InputProps={{
                              classes: { root: classes.inputRoot },
                            }}
                            InputLabelProps={{
                              classes: {
                                root: classes.labelRoot,
                                focused: classes.labelFocused,
                              },
                            }}
                            value={value}
                            onChange={(e: any) => {
                              onChange(
                                get(e, 'target.value')
                                  .trimStart()
                                  .toLowerCase(),
                              );
                            }}
                            error={!!error?.message}
                          />
                          {error?.message && (
                            <span className={classes.errorText}>
                              {error?.message}
                            </span>
                          )}
                        </Box>
                      )}
                      rules={{ required: 'Email is required' }}
                    />
                    <Typography className={classes.labelText} variant="body1">
                      Password *
                    </Typography>
                    <Controller
                      name="password"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <Box className={classes.controllerBox}>
                          <WrappedTextInput
                            type="password"
                            placeholder="Password"
                            className={classes.textValInput}
                            InputProps={{
                              classes: { root: classes.inputRoot },
                            }}
                            InputLabelProps={{
                              classes: {
                                root: classes.labelRoot,
                                focused: classes.labelFocused,
                              },
                            }}
                            value={value}
                            onChange={(e: any) =>
                              onChange(get(e, 'target.value').trimStart())
                            }
                            error={!!error?.message}
                          />
                          {error?.message && (
                            <span className={classes.errorPwd}>
                              {error?.message}
                            </span>
                          )}
                        </Box>
                      )}
                      rules={{ required: 'Password is required' }}
                    />
                    <Typography className={classes.labelText} variant="body1">
                      Links to your professional networking profiles
                    </Typography>
                    {selectedOptions.map((option, index) => {
                      return (
                        <Box
                          className={classes.selectedOpt}
                          key={`${option}-${index}`}
                        >
                          <Box className={classes.selectedOptL}>
                            <Select
                              value={selectedOptions[index]}
                              onChange={(e: any) =>
                                handleChangeOption(e.target.value, index)
                              }
                              displayEmpty
                              className={classes.selectValInput}
                              inputProps={{ 'aria-label': 'Without label' }}
                            >
                              {socialMediaOption.map(
                                (socialMedia: any, mediaIndex: number) => {
                                  if (
                                    selectedOptions.indexOf(
                                      socialMedia.value,
                                    ) === -1 ||
                                    option === socialMedia.value
                                  )
                                    return (
                                      <MenuItem
                                        value={socialMedia?.value}
                                        key={mediaIndex}
                                      >
                                        {socialMedia.text} &nbsp;&nbsp;&nbsp;
                                      </MenuItem>
                                    );
                                },
                              )}
                            </Select>
                          </Box>
                          <Box className={classes.selectedOptR}>
                            <Controller
                              name={option}
                              control={control}
                              defaultValue=""
                              render={({
                                field: { onChange, value },
                                fieldState: { error },
                              }) => (
                                <Box className={classes.controllerBox}>
                                  <WrappedTextInput
                                    type="text"
                                    placeholder="Url"
                                    className={classes.textSocialInput}
                                    InputProps={{
                                      classes: { root: classes.inputRoot },
                                      endAdornment: index > 0 && (
                                        <InputAdornment position="end">
                                          <IconButton
                                            aria-label="remove field"
                                            onClick={() =>
                                              removeSocialOption(option, index)
                                            }
                                          >
                                            <CloseIcon />
                                          </IconButton>
                                        </InputAdornment>
                                      ),
                                    }}
                                    InputLabelProps={{
                                      classes: {
                                        root: classes.labelRoot,
                                        focused: classes.labelFocused,
                                      },
                                    }}
                                    value={value}
                                    onChange={(e: any) => {
                                      onChange(get(e, 'target.value'));
                                      if (!isEmpty(get(e, 'target.value')))
                                        addSocialOption(index);
                                    }}
                                    error={!!error?.message}
                                  />
                                  {error?.message && (
                                    <span className={classes.errorUrl}>
                                      {error?.message}
                                    </span>
                                  )}
                                </Box>
                              )}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                    <Controller
                      name="termsAccepted"
                      control={control}
                      // defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <Box className={classes.checkBoxAgree}>
                          <Checkbox
                            style={{ padding: '0 9px 0 0' }}
                            checked={value}
                            // onChange={onChange}

                            onChange={(e: any) => {
                              onChange(e.target.checked);
                            }}
                          />
                          <Typography
                            variant="caption"
                            className={classes.agreeTerms}
                          >
                            Acknowledgment of{' '}
                            <a
                              href="https://www.investor.gov/introduction-investing/general-resources/news-alerts/alerts-bulletins/investor-bulletins/updated-3"
                              target="_blank"
                              rel="noreferrer"
                              className={classes.signInLink}
                            >
                              Accredited Investor
                            </a>{' '}
                            status
                          </Typography>
                          {error?.message && (
                            <span className={classes.termsText}>
                              Please read the definitions and acknowledge that
                              you are an Accredited Investor.
                            </span>
                          )}
                        </Box>
                      )}
                    />
                    <Box>
                      {message && message.length && (
                        <Typography
                          variant="body1"
                          gutterBottom
                          className={classes.errorAlready}
                        >
                          {message}
                        </Typography>
                      )}
                      <Button
                        type="submit"
                        name="Sign up"
                        className={classes.signupBtn}
                        isLoading={isLoading}
                        disabled={isLoading}
                      />
                    </Box>
                  </form>
                  <Box>
                    <Typography className={classes.tCText}>
                      By signing up, I agree to 8vdX's{' '}
                      <a
                        href="https://8vdx.com/termsandconditions/"
                        target="_blank"
                        rel="noreferrer"
                        className={classes.signInLink}
                      >
                        Terms of Service and Privacy Policy
                      </a>
                    </Typography>
                    {!referralEmailExists && (
                      <Typography className={classes.alreadyAcText}>
                        Already have account ?
                        <a
                          onClick={() => history.push('/sign-in')}
                          rel="noreferrer"
                          className={classes.signInLink}
                        >
                          {' '}
                          Sign in
                        </a>
                      </Typography>
                    )}
                    <Typography className={classes.copyText}>
                      © 2022 8vdX All rights reserved .
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>

        <SignInImage />
      </Box>
    </Box>
  );
};

export default SignUp;
